/**
 * Render Canvas2D — portrait, mobile-first. Il tracciato scorre in orizzontale
 * (modello Tiny Wings: schermo verticale, one-hand). La camera segue il rider.
 *
 * LA SCIA è la protagonista visiva: un nastro luminoso disegnato con strati additivi
 * ('lighter'), niente shadowBlur (costoso su mobile). I ghost sono scie translucide
 * di un altro colore. Art direction: "cieli infiniti e nastri di luce"
 * (vedi docs/art-direction.md).
 *
 * Questo file è SOLO presentazione: interpola lo stato della sim, non lo muta mai.
 */

import type { Terrain } from './../game/terrain';
import type { SimState } from './../game/sim';
import { mulberry32 } from './../game/rng';
import { clamp } from './../game/dmath';

const VIEW_WORLD_W = 800; // px-mondo visibili in orizzontale (aggancio scala alla larghezza)
const RIDER_SCREEN_X = 0.32; // il rider sta al 32% da sinistra
const RIDER_SCREEN_Y = 0.46; // ...e al 46% dall'alto
const TRAIL_MAX = 64; // punti della scia (comet a lunghezza fissa)
const GHOST_TRAIL_MAX = 48;

// Palette WAKE (bioma "notte neon" di default per lo Sprint 0).
const SKY_TOP = '#070a1f';
const SKY_MID = '#131a44';
const SKY_HORIZON = '#3a2a6b';
const TERRAIN_HI = '#2a2050';
const TERRAIN_LO = '#0a0c22';
const RIM = '#8be9ff';
const PLAYER_CORE = '#eafcff';
const PLAYER_GLOW = '#39d8ff';
const GHOST_GLOW = '#c86bff';

interface Pt {
  x: number;
  y: number;
}

export interface DrawOpts {
  ghost: Pt | null; // posizione interpolata del ghost (world), o null
  running: boolean; // se true, la scia cresce
  countdown: string | null; // "3","2","1","GO!" o null
  shake: number; // 0..1 intensità scossa (wipeout)
  boost: number; // 0..1 pompata efficace (premi in discesa) -> scia accesa + scintille
  cue: { text: string; down: boolean } | null; // etichetta onboarding TIENI/LASCIA
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export class WakeRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private scale = 1;
  private camY = 0;
  private camReady = false;
  private readonly trail: Pt[] = [];
  private readonly ghostTrail: Pt[] = [];
  private readonly particles: Particle[] = [];
  private readonly stars: { x: number; y: number; r: number; a: number }[] = [];

  private terrain: Terrain;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    terrain: Terrain,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D non disponibile');
    this.ctx = ctx;
    this.terrain = terrain;
    this.makeStars();
    this.resize();
  }

  setTerrain(terrain: Terrain): void {
    this.terrain = terrain;
  }

  /** Sfondo vivo del menu: cielo + stelle + terreno che scorre piano, senza rider. */
  drawBackdrop(camX: number): void {
    this.camY = this.terrain.heightAt(camX) + 120;
    this.drawSky();
    this.drawStars(camX);
    this.drawTerrain(camX);
  }

  private makeStars(): void {
    const rng = mulberry32(0x5ca1a7);
    for (let i = 0; i < 90; i++) {
      this.stars.push({ x: rng(), y: rng() * 0.7, r: rng() * 1.4 + 0.3, a: rng() * 0.5 + 0.2 });
    }
  }

  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const cssW = this.canvas.clientWidth || window.innerWidth;
    const cssH = this.canvas.clientHeight || window.innerHeight;
    this.w = Math.round(cssW * this.dpr);
    this.h = Math.round(cssH * this.dpr);
    this.canvas.width = this.w;
    this.canvas.height = this.h;
    this.scale = this.w / VIEW_WORLD_W; // px-schermo per px-mondo (aggancio alla larghezza)
  }

  reset(): void {
    this.trail.length = 0;
    this.ghostTrail.length = 0;
    this.particles.length = 0;
    this.camReady = false;
  }

  private worldToScreen(wx: number, wy: number, camX: number): Pt {
    return {
      x: (wx - camX) * this.scale + this.w * RIDER_SCREEN_X,
      y: (wy - this.camY) * this.scale + this.h * RIDER_SCREEN_Y,
    };
  }

  draw(s: SimState, opts: DrawOpts): void {
    const ctx = this.ctx;
    const camX = s.x;

    // Camera verticale morbida (solo cosmesi, mai nella sim).
    if (!this.camReady) {
      this.camY = s.y;
      this.camReady = true;
    } else {
      this.camY += (s.y - this.camY) * 0.12;
    }

    // Aggiorna le scie e le scintille della pompata.
    if (opts.running) {
      this.pushTrail(this.trail, { x: s.x, y: s.y }, TRAIL_MAX);
      if (opts.ghost) this.pushTrail(this.ghostTrail, opts.ghost, GHOST_TRAIL_MAX);
      this.updateParticles({ x: s.x, y: s.y }, opts.boost);
    }

    ctx.save();
    if (opts.shake > 0) {
      const rng = (Math.sin(s.t * 97.13) * 43758.5453) % 1; // cosmesi, fuori dalla sim
      ctx.translate((rng - 0.5) * 14 * opts.shake, (rng - 0.2) * 10 * opts.shake);
    }

    this.drawSky();
    this.drawStars(camX);
    this.drawTerrain(camX);
    if (opts.ghost) this.drawTrail(this.ghostTrail, camX, GHOST_GLOW, GHOST_GLOW, 0.5);
    this.drawParticles(camX);
    this.drawTrail(this.trail, camX, PLAYER_GLOW, PLAYER_CORE, 1 + opts.boost * 0.9);
    if (opts.ghost) this.drawGhostMarker(opts.ghost, camX);
    this.drawRider(s, camX, opts.boost);
    this.drawSpeedLines(s);
    ctx.restore();

    if (opts.cue) this.drawCue(opts.cue);
    if (opts.countdown) this.drawCountdown(opts.countdown);
  }

  private updateParticles(at: Pt, boost: number): void {
    for (const p of this.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.15;
      p.life -= 0.045;
    }
    for (let i = this.particles.length - 1; i >= 0; i--) {
      if (this.particles[i]!.life <= 0) this.particles.splice(i, 1);
    }
    // Spawn dietro il rider quando la pompata è efficace (cosmesi: Math.random ok).
    if (boost > 0.05 && this.particles.length < 90) {
      const count = 1 + Math.floor(boost * 3);
      for (let k = 0; k < count; k++) {
        this.particles.push({
          x: at.x - Math.random() * 6,
          y: at.y + (Math.random() - 0.5) * 6,
          vx: -(1 + Math.random() * 3),
          vy: (Math.random() - 0.4) * 2,
          life: 0.55 + Math.random() * 0.45,
        });
      }
    }
  }

  private drawParticles(camX: number): void {
    if (this.particles.length === 0) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const p of this.particles) {
      const sp = this.worldToScreen(p.x, p.y, camX);
      ctx.globalAlpha = Math.max(0, p.life) * 0.8;
      ctx.fillStyle = PLAYER_GLOW;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, (1.4 + p.life * 2.4) * this.dpr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawCue(cue: { text: string; down: boolean }): void {
    const ctx = this.ctx;
    const cx = this.w / 2;
    const cy = this.h * 0.2;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${20 * this.dpr}px system-ui, sans-serif`;
    const label = `${cue.down ? '▼' : '▲'}  ${cue.text}`;
    const padX = 22 * this.dpr;
    const wBox = ctx.measureText(label).width + padX * 2;
    const hBox = 44 * this.dpr;
    ctx.fillStyle = 'rgba(10,14,34,0.72)';
    ctx.strokeStyle = cue.down ? PLAYER_GLOW : GHOST_GLOW;
    ctx.lineWidth = 2 * this.dpr;
    this.roundRect(cx - wBox / 2, cy - hBox / 2, wBox, hBox, 14 * this.dpr);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#eafcff';
    ctx.fillText(label, cx, cy + 1);
    ctx.restore();
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  private pushTrail(buf: Pt[], p: Pt, max: number): void {
    const last = buf[buf.length - 1];
    if (last && Math.abs(last.x - p.x) < 1 && Math.abs(last.y - p.y) < 1) return;
    buf.push({ x: p.x, y: p.y });
    if (buf.length > max) buf.shift();
  }

  private drawSky(): void {
    const ctx = this.ctx;
    const g = ctx.createLinearGradient(0, 0, 0, this.h);
    g.addColorStop(0, SKY_TOP);
    g.addColorStop(0.55, SKY_MID);
    g.addColorStop(1, SKY_HORIZON);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.w, this.h);
  }

  private drawStars(camX: number): void {
    const ctx = this.ctx;
    const par = (camX * 0.02) % this.w;
    for (const st of this.stars) {
      let x = st.x * this.w - par;
      if (x < 0) x += this.w;
      ctx.globalAlpha = st.a;
      ctx.fillStyle = '#cfe0ff';
      ctx.beginPath();
      ctx.arc(x, st.y * this.h, st.r * this.dpr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  private drawTerrain(camX: number): void {
    const ctx = this.ctx;
    const stepPx = 4 * this.dpr;
    const leftWorld = camX - (this.w * RIDER_SCREEN_X) / this.scale;

    ctx.beginPath();
    let first = true;
    let topAt0 = 0;
    for (let px = 0; px <= this.w + stepPx; px += stepPx) {
      const wx = leftWorld + px / this.scale;
      const p = this.worldToScreen(wx, this.terrain.heightAt(wx), camX);
      if (first) {
        topAt0 = p.y;
        ctx.moveTo(p.x, p.y);
        first = false;
      } else {
        ctx.lineTo(p.x, p.y);
      }
    }
    ctx.lineTo(this.w, this.h);
    ctx.lineTo(0, this.h);
    ctx.closePath();

    const g = ctx.createLinearGradient(0, topAt0 - 40 * this.dpr, 0, this.h);
    g.addColorStop(0, TERRAIN_HI);
    g.addColorStop(1, TERRAIN_LO);
    ctx.fillStyle = g;
    ctx.fill();

    // Cresta luminosa (rim) sopra il terreno.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = RIM;
    ctx.lineWidth = 2 * this.dpr;
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    first = true;
    for (let px = 0; px <= this.w + stepPx; px += stepPx) {
      const wx = leftWorld + px / this.scale;
      const p = this.worldToScreen(wx, this.terrain.heightAt(wx), camX);
      if (first) {
        ctx.moveTo(p.x, p.y);
        first = false;
      } else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.restore();
  }

  /** Nastro luminoso a strati additivi, con taper testa->coda. */
  private drawTrail(buf: Pt[], camX: number, glow: string, core: string, intensity: number): void {
    if (buf.length < 2) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const passes = [
      { w: 16, a: 0.10, color: glow },
      { w: 8, a: 0.18, color: glow },
      { w: 3.2, a: 0.95, color: core },
    ];
    for (const pass of passes) {
      for (let i = 1; i < buf.length; i++) {
        const age = i / buf.length; // 0 coda -> 1 testa
        const a = pass.a * age * age * intensity;
        if (a <= 0.01) continue;
        const p0 = this.worldToScreen(buf[i - 1]!.x, buf[i - 1]!.y, camX);
        const p1 = this.worldToScreen(buf[i]!.x, buf[i]!.y, camX);
        ctx.globalAlpha = a;
        ctx.strokeStyle = pass.color;
        ctx.lineWidth = pass.w * age * this.dpr;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawGhostMarker(ghost: Pt, camX: number): void {
    const ctx = this.ctx;
    const p = this.worldToScreen(ghost.x, ghost.y, camX);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = GHOST_GLOW;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 6 * this.dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawRider(s: SimState, camX: number, boost: number): void {
    const ctx = this.ctx;
    const p = this.worldToScreen(s.x, s.y, camX);
    const wiping = s.wipeTimer > 0;

    ctx.save();
    ctx.translate(p.x, p.y);

    // Alone (si gonfia quando la pompata è efficace).
    ctx.globalCompositeOperation = 'lighter';
    const r = (22 + boost * 16) * this.dpr;
    const halo = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    halo.addColorStop(0, wiping ? 'rgba(255,120,120,0.9)' : 'rgba(120,240,255,0.9)');
    halo.addColorStop(1, 'rgba(120,240,255,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();

    // Corpo (piccolo, la scia è la star).
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = wiping ? '#ff8a8a' : PLAYER_CORE;
    ctx.beginPath();
    ctx.arc(0, 0, 5 * this.dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawSpeedLines(s: SimState): void {
    const speed = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
    const t = clamp((speed - 700) / 1000, 0, 1);
    if (t <= 0.02) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = t * 0.5;
    ctx.strokeStyle = '#bfefff';
    ctx.lineWidth = 1.4 * this.dpr;
    for (let i = 0; i < 6; i++) {
      const y = ((i * 137 + (s.x * 0.5) % this.h) % this.h);
      const len = 40 * this.dpr * t;
      ctx.beginPath();
      ctx.moveTo(this.w - i * 30 * this.dpr, y);
      ctx.lineTo(this.w - i * 30 * this.dpr - len, y);
      ctx.stroke();
    }
    // Vignetta velocità.
    const vg = ctx.createRadialGradient(this.w / 2, this.h / 2, this.h * 0.3, this.w / 2, this.h / 2, this.h * 0.7);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, `rgba(0,0,10,${t * 0.4})`);
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.restore();
  }

  private drawCountdown(txt: string): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = RIM;
    ctx.font = `700 ${120 * this.dpr}px system-ui, sans-serif`;
    ctx.globalAlpha = 0.9;
    ctx.fillText(txt, this.w / 2, this.h * 0.4);
    ctx.restore();
  }
}
